import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover/popover";

import { useTakeActionModal } from "./use-action-modal";
import { Button } from "@/components/ui/button/button";
import {
  changeStatusOfLead,
  createLeadTriggerAction,
} from "@/config/mutation/employee/lead";
import { Textarea } from "@/components/ui/textarea/textarea";
import { Label } from "@/components/ui/label/label";
import { DateTimePicker } from "@/components/ui/datetime-picker";

export const TakeActionOnLeadModal = () => {
  const queryClient = useQueryClient();
  const key = Cookies.get("key") as string;
  const { open, onOpenChange, lead, setLead } = useTakeActionModal();

  const [comments, setComments] = useState("");
  const [date, setDate] = useState<Date>();

  const [status, setStatus] = useState("");
  const [subStatus, setSubStatus] = useState("");

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isSubStatusOpen, setIsSubStatusOpen] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: changeStatusOfLead(key),
  });

  const { mutateAsync, isPending: isTriggerPending } = useMutation({
    mutationFn: createLeadTriggerAction(key),
  });

  const submitAction = async () => {
    if (!comments || comments === undefined) {
      return toast.error("Please write comment first.");
    }

    if (!subStatus) {
      return toast.error("Please choose status.");
    }

    console.log("Date", date);

    mutate(
      {
        lead: lead?.id as string,
        comments,
        status: subStatus as string,
      },
      {
        onSuccess: async () => {
          if (date !== undefined) {
            await mutateAsync({
              lead: lead?.id as string,
              date: date as Date,
              comments,
            });
          }
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["leads"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["lead-logs"],
          });

          toast.success("Action Taken.");
          onOpenChange(false);
          setComments("");
          setStatus("");
          setSubStatus("");
        },

        onError: (error) => {
          console.log("ERROR WHILE CHANGING STATUS OF LEAD", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const statusList = [
    {
      label: "Closed (Success)",
      value: "Closed",
    },
    {
      label: "Not Interested",
      value: "Not Interested",
    },
    {
      label: "Call Not Connected",
      value: "Call Not Connected",
    },
    {
      label: "Call disconnect",
      value: "Call disconnect",
    },
    {
      label: "Will Proceed",
      value: "Will Proceed",
    },
    {
      label: "Documents Issues",
      value: "Documents Issues",
    },
    {
      label: "Call Back",
      value: "Call Back",
    },
    {
      label: "Repeated Leads",
      value: "Repeated Leads",
    },
  ];

  const subStatusListMapping: {
    [key: string]: {
      value: string;
      label: string;
    }[];
  } = {
    Closed: [
      {
        label: "Closed (Success)",
        value: "Closed",
      },
    ],
    "Not Interested": [
      {
        label: "Not Interested",
        value: "Not Interested",
      },
      {
        label: "Proceed from Another Number",
        value: "Proceed from Another Number",
      },
      {
        label: "Account open in other broker",
        value: "Account open in other broker",
      },
      {
        label: "Mobile Number not Linked",
        value: "Mobile Number not Linked",
      },
      {
        label: "MINOR",
        value: "MINOR",
      },
      {
        label: "Pan/Adhar Card Not Linked",
        value: "Pan/Adhar Card Not Linked",
      },
    ],
    "Call Not Connected": [
      {
        label: "Ringing",
        value: "Ringing",
      },
      {
        label: "Busy",
        value: "Busy",
      },
      {
        label: "WAITING",
        value: "WAITING",
      },
      {
        label: "Switch Off",
        value: "Switch Off",
      },
      {
        label: "OUT OF NETWORK",
        value: "OUT OF NETWORK",
      },
      {
        label: "Invalid No",
        value: "Invalid No",
      },
    ],

    "Call disconnect": [
      {
        label: "Call disconnect",
        value: "Call disconnect",
      },
    ],

    "Will Proceed": [
      {
        label: "15 DAYS",
        value: "15 DAYS",
      },
      {
        label: "30 DAYS",
        value: "30 DAYS",
      },
      {
        label: "MONTH",
        value: "MONTH",
      },
      {
        label: "DECISION PENDING",
        value: "DECISION PENDING",
      },
    ],

    "Documents Issues": [
      {
        label: "KYC ISSUE",
        value: "KYC ISSUE",
      },
      {
        label: "BANK ERROR",
        value: "BANK ERROR",
      },
      {
        label: "IPV",
        value: "IPV",
      },
      {
        label: "AADHAR OTP",
        value: "AADHAR OTP",
      },
      {
        label: "NAME MISMATCH",
        value: "NAME MISMATCH",
      },
    ],

    "Call Back": [
      {
        label: "Call Back",
        value: "Call Back",
      },
    ],

    "Repeated Leads": [
      {
        label: "Repeated Leads",
        value: "Repeated Leads",
      },
    ],
  };

  const subStatusList = useMemo(() => {
    if (!Object.keys(subStatusListMapping)?.includes(status)) return [];

    const tempList = subStatusListMapping[status];

    return tempList;
  }, [status]);

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(open) => {
          onOpenChange(open);
          setLead(null);
          setComments("");
          setDate(undefined);
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Take action on lead</DialogTitle>
          </DialogHeader>

          <div className="flex">
            <div className="flex-1 flex flex-col gap-5 px-4">
              <div className="flex flex-col">
                <div className="text-[14px] font-medium">Status</div>
                <Popover open={isStatusOpen} onOpenChange={setIsStatusOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex data-[state=open]:bg-muted"
                    >
                      {status
                        ? statusList?.find((sl) => sl?.value === status)?.label
                        : "Select a status"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="mr-8">
                    <Command>
                      <CommandInput placeholder="search status..." />
                      <CommandList className="mt-2 [scrollbar-width:thin]">
                        <CommandEmpty>No results found.</CommandEmpty>
                        <>
                          {statusList.map((item) => {
                            return (
                              <CommandItem
                                key={item.value}
                                disabled={item.value === status}
                                onMouseDown={() => {
                                  setStatus(item.value);
                                  setIsStatusOpen(false);
                                }}
                              >
                                {item.label}
                              </CommandItem>
                            );
                          })}
                        </>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex flex-col">
                <div className="text-[14px] font-medium">Sub Status</div>
                <Popover
                  open={isSubStatusOpen}
                  onOpenChange={setIsSubStatusOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex data-[state=open]:bg-muted"
                    >
                      {subStatus
                        ? subStatusList?.find((sl) => sl?.value === subStatus)
                            ?.label
                        : "Select sub status"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="mr-8">
                    <Command>
                      <CommandInput placeholder="search status..." />
                      <CommandList className="mt-2 [scrollbar-width:thin]">
                        <CommandEmpty>No results found.</CommandEmpty>
                        <>
                          {subStatusList?.map((item) => {
                            return (
                              <CommandItem
                                key={item.value}
                                disabled={item.value === subStatus}
                                onMouseDown={() => {
                                  setSubStatus(item.value);
                                  setIsSubStatusOpen(false);
                                }}
                              >
                                {item.label}
                              </CommandItem>
                            );
                          })}
                        </>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="flex-1 border-l pl-6">
              <div className="mt-5 space-y-3">
                <div>
                  <Label htmlFor="comments">Comment</Label>
                  <Textarea
                    name="comments"
                    value={comments}
                    onChange={(e) => {
                      setComments(e.target.value);
                    }}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label>When to remind you?</Label>

                  <DateTimePicker value={date} onChange={setDate} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Button
              onClick={submitAction}
              disabled={isPending || isTriggerPending}
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
